import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Clock, Loader2, Mail, Lock, AlertCircle } from 'lucide-react';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login, googleLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/dashboard';
  const stateError = location.state?.error;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(email, password);
    if (result.success) {
      navigate(from, { replace: true });
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8 justify-center" data-testid="login-logo">
          <div className="w-12 h-12 bg-[#ffd703] border-3 border-black flex items-center justify-center">
            <Clock className="w-7 h-7 text-black" />
          </div>
          <span className="font-bold text-2xl text-black font-sans">New Community Wealth Incubator</span>
        </Link>

        <div className="retro-card relative">
          {/* Window header */}
          <div className="retro-card-header">
            <div className="window-dots">
              <div className="window-dot window-dot-1"></div>
              <div className="window-dot window-dot-2"></div>
              <div className="window-dot window-dot-3"></div>
            </div>
            <span className="sparkle animate-sparkle">✦</span>
            <span className="font-sans font-semibold text-black">sign in</span>
          </div>
          
          <div className="p-6">
            <h1 className="text-2xl font-bold text-black mb-2">Welcome back</h1>
            <p className="text-black/60 font-sans text-sm mb-6">
              Sign in to your account to continue
            </p>

            {(error || stateError) && (
              <div className="mb-4 p-3 rounded-md bg-red-100 border-2 border-black flex items-start gap-2" data-testid="login-error">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-600 font-sans">{error || stateError}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="font-sans font-semibold">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black/50" />
                  <input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="retro-input w-full pl-10 pr-4 py-2 font-sans"
                    required
                    data-testid="login-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="font-sans font-semibold">Password</Label>
                  <Link to="/forgot-password" className="text-xs text-[#8b5cf6] hover:underline font-sans font-semibold" data-testid="forgot-password-link">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black/50" />
                  <input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="retro-input w-full pl-10 pr-4 py-2 font-sans"
                    required
                    data-testid="login-password"
                  />
                </div>
              </div>

              <button 
                type="submit" 
                className="retro-btn w-full py-3 font-sans"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Signing in...
                  </span>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>

            <div className="relative my-6">
              <div className="border-t-2 border-black"></div>
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#fef9e7] px-3 text-xs text-black/50 font-sans font-semibold">
                OR
              </span>
            </div>

            <button
              type="button"
              className="retro-btn retro-btn-outline w-full py-3 font-sans flex items-center justify-center gap-2"
              onClick={googleLogin}
              data-testid="login-google"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            <p className="text-center text-sm text-black/60 mt-6 font-sans">
              Don't have an account?{' '}
              <Link to="/register" className="text-[#e056a0] hover:underline font-semibold" data-testid="login-register-link">
                Create one
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
